import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Plus,
  MoreVertical,
  ExternalLink,
  Trash2,
  FileText,
  Calendar,
  User,
  LogIn,
} from "lucide-react";
import { cn, getScoreColor, getScoreLabel, formatDateTime } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

function AuditCard({ audit, isSuperAdmin, userId, onNavigate, onDelete }) {
  const progressPercentage = audit.total_questions > 0
    ? (audit.answered_questions / audit.total_questions) * 100
    : 0;

  return (
    <Card
      className="card-glass card-hover cursor-pointer group"
      onClick={() => onNavigate(`/dashboard/${audit.id}`)}
      data-testid={`audit-card-${audit.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="heading-md text-white mb-1 line-clamp-1">
              {audit.client_info.store_name}
            </h3>
            <a
              href={audit.client_info.store_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-zinc-500 hover:text-primary flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {audit.client_info.store_url.replace(/^https?:\/\//, '').slice(0, 25)}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`audit-menu-${audit.id}`}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
              <DropdownMenuItem
                onClick={(e) => { e.stopPropagation(); onNavigate(`/report/${audit.id}`); }}
                className="text-zinc-300 focus:bg-zinc-800"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Report
              </DropdownMenuItem>
              {(isSuperAdmin || audit.user_id === userId) && (
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onDelete(audit.id); }}
                  className="text-red-400 focus:bg-zinc-800 focus:text-red-400"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Score */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <p className="text-sm text-zinc-500 mb-1">Overall Score</p>
            <div className="flex items-baseline gap-2">
              <span className={cn("text-3xl font-bold", getScoreColor(audit.overall_score))}>
                {Math.round(audit.overall_score)}
              </span>
              <span className="text-zinc-500">/100</span>
            </div>
          </div>
          <Badge className={cn(
            audit.overall_score >= 75 ? "score-badge-high" :
            audit.overall_score >= 50 ? "score-badge-medium" : "score-badge-low"
          )}>
            {getScoreLabel(audit.overall_score)}
          </Badge>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-500">Progress</span>
            <span className="text-zinc-400">{audit.answered_questions}/{audit.total_questions}</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progressPercentage}%` }} />
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{audit.client_info.auditor_name}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>{formatDateTime(audit.created_at)}</span>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <Badge
            variant="outline"
            className={cn(
              "w-full justify-center",
              audit.status === "completed"
                ? "border-green-500/30 text-green-400"
                : "border-yellow-500/30 text-yellow-400"
            )}
          >
            {audit.status === "completed" ? "Completed" : "In Progress"}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}


export default function SavedAudits() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isSuperAdmin, loading: authLoading, getGoogleAuthUrl } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (isAuthenticated) {
        fetchAudits();
      } else {
        setLoading(false);
      }
    }
  }, [authLoading, isAuthenticated]);

  const fetchAudits = async () => {
    try {
      const response = await axios.get(`${API}/audits`);
      setAudits(response.data);
    } catch (error) {
      console.error("Error fetching audits:", error);
      toast.error("Failed to load audits");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/audits/${deleteId}`);
      setAudits(audits.filter((a) => a.id !== deleteId));
      toast.success("Audit deleted successfully");
    } catch (error) {
      console.error("Error deleting audit:", error);
      toast.error("Failed to delete audit");
    } finally {
      setDeleteId(null);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  // Guest users: prompt to sign in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 bg-grid">
        <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/")}
                  className="text-zinc-400 hover:text-white"
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <img src={LOGO_URL} alt="Digital Impressions" className="h-8 w-auto" />
                <h1 className="heading-sm text-white">Saved Audits</h1>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-zinc-900 flex items-center justify-center">
              <LogIn className="w-10 h-10 text-zinc-600" />
            </div>
            <h2 className="heading-md text-white mb-2">Sign in to View Saved Audits</h2>
            <p className="text-zinc-500 mb-6 max-w-md mx-auto">
              Sign in with your Google account to access your saved audits, track progress, and manage your reports.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button
                onClick={() => { window.location.href = getGoogleAuthUrl(); }}
                className="bg-white hover:bg-zinc-100 text-zinc-900 font-medium h-12 px-6"
                data-testid="login-to-view-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign In with Google
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-12"
                data-testid="start-new-audit-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start New Audit
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 bg-grid">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src={LOGO_URL} alt="Digital Impressions" className="h-8 w-auto" />
              <div>
                <h1 className="heading-sm text-white flex items-center gap-2">
                  {isSuperAdmin ? "All Audits" : "My Audits"}
                  {isSuperAdmin && (
                    <Badge className="bg-primary/20 text-primary border-primary/30 text-xs" data-testid="super-admin-badge">
                      Super Admin
                    </Badge>
                  )}
                </h1>
                <p className="text-xs text-zinc-500">
                  {audits.length} audit{audits.length !== 1 ? 's' : ''}
                  {isSuperAdmin && ' across all users'}
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/")}
              className="btn-primary"
              data-testid="new-audit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Audit
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {audits.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-zinc-900 flex items-center justify-center">
              <FileText className="w-10 h-10 text-zinc-600" />
            </div>
            <h2 className="heading-md text-white mb-2">No Audits Yet</h2>
            <p className="text-zinc-500 mb-6">Start your first IMPRESS 360 Audit</p>
            <Button
              onClick={() => navigate("/")}
              className="btn-primary"
              data-testid="create-first-audit-btn"
            >
              Create First Audit
            </Button>
          </div>
        ) : isSuperAdmin ? (
          // Super Admin view: group by brand manager
          (() => {
            const grouped = {};
            audits.forEach((audit) => {
              const key = audit.user_email || audit.user_id || "unknown";
              if (!grouped[key]) {
                grouped[key] = {
                  name: audit.user_name || audit.user_email || "Unknown User",
                  email: audit.user_email || "",
                  isMe: audit.user_id === user?.user_id,
                  audits: [],
                };
              }
              grouped[key].audits.push(audit);
            });

            // Sort: own audits first, then alphabetical by name
            const sortedGroups = Object.values(grouped).sort((a, b) => {
              if (a.isMe) return -1;
              if (b.isMe) return 1;
              return a.name.localeCompare(b.name);
            });

            return (
              <div className="space-y-10">
                {sortedGroups.map((group) => (
                  <div key={group.email || group.name} data-testid={`group-${group.email}`}>
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-zinc-800">
                      <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                        <User className="w-4 h-4 text-zinc-400" />
                      </div>
                      <div>
                        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                          {group.name}
                          {group.isMe && (
                            <Badge variant="outline" className="text-xs border-primary/30 text-primary">You</Badge>
                          )}
                        </h2>
                        {group.email && !group.isMe && (
                          <p className="text-xs text-zinc-500">{group.email}</p>
                        )}
                      </div>
                      <span className="ml-auto text-xs text-zinc-500">
                        {group.audits.length} audit{group.audits.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.audits.map((audit) => (
                        <AuditCard
                          key={audit.id}
                          audit={audit}
                          isSuperAdmin={isSuperAdmin}
                          userId={user?.user_id}
                          onNavigate={navigate}
                          onDelete={setDeleteId}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audits.map((audit) => (
              <AuditCard
                key={audit.id}
                audit={audit}
                isSuperAdmin={isSuperAdmin}
                userId={user?.user_id}
                onNavigate={navigate}
                onDelete={setDeleteId}
              />
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Audit?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone. This will permanently delete the audit
              and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
